import { useEffect, useState, useRef, useCallback } from "react";
import styles from "./restosList.module.scss";
import { MarkerData } from "Types/markers";
import styled, { useTheme } from "styled-components";
import { device } from "../breakpoints";
import { Button } from "@mui/material";
import locales from "locales/locales";
import FavoriteIcon from "@mui/icons-material/Favorite";

declare global {
  interface Window {
    dataLayer: any;
  }
}

function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  var R = 6371; // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1); // deg2rad below
  var dLon = deg2rad(lon2 - lon1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c; // Distance in km
  return d;
}

function deg2rad(deg: number) {
  return (deg * Math.PI) / 180;
}

function getDistanceStr(meters: number) {
  if (meters < 1000) {
    return (Math.round(meters / 10) * 10).toString() + "m";
  } else if (meters < 4000) {
    return (Math.round(meters / 100) / 10).toString() + "km";
  } else {
    return Math.round(meters / 1000).toString() + "km";
  }
}

export const RestosList = ({
  restos,
  publisher,
  handleClick,
  userLocation,
}: {
  restos: MarkerData[];
  publisher: string;
  handleClick: (marker: MarkerData) => void;
  userLocation: { lat: number; lng: number } | null | undefined;
}) => {
  const countStep = 12;
  const [showCount, setShowCount] = useState(countStep);
  const [sortedRestos, setSortedRestos] = useState<MarkerData[]>([]);
  const loader = useRef(null);
  const theme = useTheme();

  // If there is a new list of restos or a new user location, add the distance to the user and sort them
  useEffect(() => {
    if (userLocation) {
      const list = restos.map((resto) => ({
        ...resto,
        distance: getDistanceFromLatLonInKm(
          userLocation.lat,
          userLocation.lng,
          resto.latitude,
          resto.longitude
        ),
      }));

      setSortedRestos(list.sort((a, b) => a.distance - b.distance));
    } else {
      if (publisher === "tdg") {
        const list = restos.map((resto) => ({
          ...resto,
          d: resto.canton === "Genève" ? (resto.image ? 1 : 2) : 4,
        }));

        setSortedRestos(list.sort((a, b) => a.d - b.d));
      } else {
        const list = restos.map((resto) => ({
          ...resto,
          d: resto.canton === "Vaud" ? (resto.image ? 1 : 2) : 4,
        }));

        setSortedRestos(list.sort((a, b) => a.d - b.d));
      }
    }
  }, [restos, userLocation, publisher]);

  return (
    <Container>
      <ListContent>
        {sortedRestos.slice(0, showCount).map((resto) => (
          <ItemContainer
            publisher={publisher}
            className={resto.main_style}
            key={`resto-${resto.markerId}`}
            onClick={() => {
              handleClick(resto);

              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: "Interactions",
                event_action: "click",
                event_label: `showLocation:${resto.markerId}`,
              });
            }}
          >
            <ImageContainer>
              <div
                style={{
                  backgroundPosition: "bottom center",
                  backgroundImage: `url(${
                    resto.image
                      ? resto.image
                      : "https://dev-interaktiv-mf.tagesanzeiger.ch/2024/map-generic/teaser-600x400.jpg"
                  })`,
                }}
                className={styles.image}
              />
            </ImageContainer>

            <ItemContent>
              <div>
                <ItemTitle>{resto.name}</ItemTitle>
                <ItemDates>{resto?.date_str && resto?.date_str}</ItemDates>
                {/* <ItemDescription>
                  {resto?.address && resto?.address}
                </ItemDescription> */}
                {resto?.distance && (
                  <ItemDistance>
                    {getDistanceStr(resto?.distance * 1000)}
                  </ItemDistance>
                )}
              </div>
            </ItemContent>
          </ItemContainer>
        ))}
      </ListContent>
      <div
        ref={loader}
        style={{ padding: "20px", display: "flex", justifyContent: "center" }}
      >
        {sortedRestos.length > showCount && (
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setShowCount(showCount + countStep);

              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: "Interactions",
                event_action: "click",
                event_label: `showmore`,
              });
            }}
          >
            {locales.buttonSeeMore[theme.lang as "fr" | "de"]}
          </Button>
        )}
      </div>
    </Container>
  );
};

const ItemContent = styled.div`
  width: 100%;
  background: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  padding: 15px;
`;

const ItemTitle = styled.div`
  padding-left: 1rem;
  font-family: var(--font-serif);
  font-weight: 600;
  font-size: 1em;
  line-height: 1.2em;
  margin-bottom: 0.5em;
  color: ${(props) => props.theme.textColor};
`;

const ItemDistance = styled.div`
  padding-left: 1rem;
  font-size: 0.8em;
  color: #a2a3a5;
`;
const ItemDates = styled.div`
  padding-left: 1rem;
  font-family: var(--font-sans-serif);
  overflow: hidden;
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  color: var(--gray-2);
`;
const ItemDescription = styled.div`
  padding-left: 1rem;
  font-family: var(--font-sans-serif);
  overflow: hidden;
  width: calc(100%);
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  text-overflow: ellipsis;
  display: block;
  white-space: nowrap;
  margin-top: 0.3rem;
  margin-bottom: 0.3rem;
  color: var(--gray-2);
`;
const ItemStyles = styled.div`
  padding-left: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const Container = styled.div`
  position: relative;
`;
const ListContent = styled.div`
  position: relative;
  display: grid;
  padding: 1em;
  padding-top: 0;
  grid-auto-rows: 1fr 1fr;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-gap: 30px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }

  @media ${device.laptopL} {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 20px;
  }
`;

const ItemContainer = styled.div<{ publisher?: string }>`
  display: grid;
  position: relative;
  cursor: pointer;
  width: 100%;
  overflow: hidden;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
  -moz-border-radius: 10px;
  border-radius: 10px;

  &:hover {
    opacity: 0.9;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(min-content, max-content) 1fr;
  }
`;

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.theme.mainColor};
  background-size: cover;
  background-position: left center;
`;

const FavoriteLabel = styled.span<{ publisher?: string }>`
  display: inline-block;
  opacity: 1;
  color: ${(props) =>
    props.publisher === "24heures" ? "var(--tenant-24h)" : "var(--tenant-tdg)"};
`;
